import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  type ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  type Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  DateFnsAdapter,
  MAT_DATE_FNS_FORMATS,
  provideDateFnsAdapter,
} from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { type AppSettings } from '@core/config/app-settings.model';
import { provideAppSettings } from '@core/config/settings.providers';
import { SettingsService } from '@core/config/settings.service';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { errorInterceptor } from '@core/interceptors/error-handler.interceptor';
import {
  createErrorHandler as SentryCreateErrorHandler,
  TraceService as SentryTraceService,
} from '@sentry/angular';
import { AuthService } from '@services/auth/auth.service';
import { PusherService } from '@services/pusher/pusher.service';
import { PwaService } from '@services/pwa/pwa.service';
import { UserService } from '@services/user/user.service';
import { enUS } from 'date-fns/locale';
import { provideNgxStripe } from 'ngx-stripe';
import { APPRoutes, routes } from './app.routes';
import { AuthRoutes } from './features/auth/auth.routes';

// Provider to override the default DatePipe
export const EXACT_DATE_PIPE_PROVIDER: Provider[] = [
  { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'UTC' } },
];

export function createAppConfig(settings: AppSettings): ApplicationConfig {
  return {
    providers: [
      provideAppSettings(settings),
      provideServiceWorker('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(
        routes,
        withInMemoryScrolling({
          scrollPositionRestoration: 'top',
        })
      ),
      provideNgxStripe(settings.stripe.publishableKey),
      provideAnimationsAsync(),
      provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor])),
      { provide: MAT_DATE_LOCALE, useValue: enUS },
      { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
      { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
      { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
      {
        provide: MAT_DIALOG_DEFAULT_OPTIONS,
        useValue: { autoFocus: 'dialog', restoreFocus: true },
      },
      provideDateFnsAdapter(),
      provideAppInitializer(() => {
        const pwaService = inject(PwaService);
        const router = inject(Router);
        const shouldShowPrompt = () => {
          return router.url.includes(`/${AuthRoutes.WELCOME}`);
        };

        pwaService.initialize(shouldShowPrompt);
      }),
      provideAppInitializer(() => {
        const authService = inject(AuthService);
        const userService = inject(UserService);

        if (authService.isAuthenticated()) {
          return userService.getCurrentUserInfo();
        }

        return Promise.resolve();
      }),
      provideAppInitializer(() => {
        const maintenance = inject(SettingsService).maintenanceMode;

        if (maintenance) {
          const authService = inject(AuthService);
          const router = inject(Router);

          authService.logout();
          router.navigate([APPRoutes.MAINTENANCE]);
        }
      }),
      provideAppInitializer(() => {
        const pusherService = inject(PusherService);
        pusherService.initPusher();
      }),
      [
        {
          provide: ErrorHandler,
          useValue: SentryCreateErrorHandler({
            showDialog: false,
          }),
        },
        {
          provide: SentryTraceService,
          deps: [Router],
        },
      ],
      EXACT_DATE_PIPE_PROVIDER,
    ],
  };
}
